import React, { useEffect, useState } from 'react';
import { Box, makeStyles, Typography } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { tokenRequests } from '../../api/api';
import { CompanyLogo } from '../../components/CompanyLogo';
import { PoweredBy } from '../../components/PoweredBy';
import { Spinner } from '../../components/Spinner';
import { trackButtonClicked, trackPage, useAnalyticsHomeownerParams } from '../../hooks/useEventTracking';
import { useOrgData } from '../../hooks/useOrgData';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { GooglePlacesObject } from '../../utils/googlePlacesObject';

import { ServiceAreaAddressField } from './ServiceAreaAdressField';

const useStyles = makeStyles((theme: HCPTheme) => ({
  actionButtonsWrapper: {
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    gap: `14px`,
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '100%',
    zIndex: theme.zIndex.snackbar,
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
    boxShadow: theme.shadows[4],
    flexDirection: 'column',
  },
  actionButtons: {
    display: 'flex',
    gap: `${theme.spacing(2)}px`,
    width: '100%',
  },
}));

type Props = {
  children: React.ReactNode;
};

type Status = 'loading' | 'error' | 'success' | 'idle';

export const ServiceAreaWrapperV2 = ({ children }: Props) => {
  const { state, dispatch, actions } = useOnlineBookingReducer();
  const { token } = state;

  const [fieldValue, setFieldValue] = useState<string>('');
  const [selectedAddress, setSelectedAddress] = useState<GooglePlacesObject | null>(null);
  const [validationError, setValidationError] = useState<boolean>(false);
  const { actionButtons, actionButtonsWrapper } = useStyles();
  const { data: orgData } = useOrgData({ token });

  const zipCodesEnabled = orgData?.zipCodesEnabled;
  const orgName = orgData?.name;
  const phoneNumber = orgData?.phoneNumber;

  const [status, setStatus] = useState<Status>('idle');

  const trackingProps = useAnalyticsHomeownerParams();

  const onChange = (value: string) => {
    setValidationError(false);
    if (value === '') {
      setSelectedAddress(null);
    }
    setFieldValue(value);
  };

  const onAddressSelect = (place: GooglePlacesObject) => {
    setValidationError(false);
    setSelectedAddress(place);
    setFieldValue(place.formatted_address);
  };

  const handleClick = async () => {
    setValidationError(false);
    trackButtonClicked({ buttonName: 'verify address', ...trackingProps });
    if (selectedAddress) {
      setStatus('loading');
      tokenRequests
        .checkAddress(token, fieldValue)
        .then((response) => {
          if (response?.data?.serviceable) {
            setStatus('success');
            dispatch(
              actions.updateContactDetailsAddress({
                state: selectedAddress.state,
                city: selectedAddress.city,
                zipCode: selectedAddress.postal_code,
                country: selectedAddress.country === 'US' ? 'United States' : 'Canada',
                addressLine1: selectedAddress.street,
                addressLine2: selectedAddress.street_line_2 || '',
              })
            );
          } else {
            setStatus('error');
          }
        })
        .catch(() => {
          setStatus('error');
        });
    } else {
      setValidationError(true);
    }
  };

  const handleBackClick = () => {
    setStatus('idle');
  };

  useEffect(() => {
    if (trackingProps && status === 'error') {
      trackPage('Online Booking - Service area error page', {
        ...trackingProps,
      });
    }
  }, [trackingProps, status]);

  if (!zipCodesEnabled || status === 'success') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return (
    <Box overflow="auto" maxHeight="100%" height="100%" mx={-3} px={2}>
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100%"
        alignItems="center"
        justifyContent="center"
        pt={5}
        pb={8}
      >
        <Box mb={1}>
          <CompanyLogo top={false} />
        </Box>
        <Typography variant="h5" align="center" gutterBottom>
          Welcome {orgName ? `to ${orgName}` : ''}
        </Typography>
        {status === 'error' ? (
          <Typography variant="body1" align="center">
            We currently don’t operate in <br />
            <b>{selectedAddress?.formatted_address}</b>
            {phoneNumber ? <>, if you have any questions please call: {phoneNumber}</> : ''}
          </Typography>
        ) : (
          <>
            <Typography variant="body1" align="center" gutterBottom>
              Let us check if we operate in your area
            </Typography>
            <Box ml={4} mr={4} mt={1} display="flex" width="100%" maxWidth={350}>
              <ServiceAreaAddressField
                onAddressSelect={onAddressSelect}
                onChange={onChange}
                value={fieldValue}
                error={validationError}
              />
            </Box>
          </>
        )}
      </Box>
      <Box className={actionButtonsWrapper}>
        <Box className={actionButtons}>
          <Button
            version="v2"
            onClick={status !== 'error' ? handleClick : handleBackClick}
            size="medium"
            fullWidth
            variant="contained"
            color="secondary"
          >
            {status !== 'error' ? 'Verify address' : 'Back'}
          </Button>
        </Box>
        <PoweredBy />
      </Box>

      <Spinner show={status === 'loading'} />
    </Box>
  );
};
